/**
 * InOut — what a person writes in their record (InOut plan §8, Lane 6).
 *
 * Shapes only: dates are calendar days (`YYYY-MM-DD`) and their order is
 * checked here; every value of the country's own vocabulary (regime, subtype,
 * justification, evidence item, notice options) is checked by the API against
 * the country's rules.
 */
import { z } from 'zod';

const isoDate = z.iso.date();
const countryCode = z.string().regex(/^[A-Z]{2}$/);
const vocabulary = z.string().min(1).max(80);

/** A title the person holds — named in the vocabulary of the country that issued it. */
export const inoutTitleCreateSchema = z
  .object({
    countryCode,
    regime: vocabulary,
    subtype: vocabulary.nullable().default(null),
    issuedOn: isoDate,
    /** Null for a status with no expiry. */
    validUntil: isoDate.nullable(),
    /** The title it renews. */
    previousTitleId: z.uuid().nullable().default(null),
  })
  .refine((t) => t.validUntil === null || t.validUntil >= t.issuedOn, {
    path: ['validUntil'],
    message: 'validUntil is before issuedOn',
  });
export type InOutTitleCreateInput = z.infer<typeof inoutTitleCreateSchema>;

/** A correction to a title — its country never changes. */
export const inoutTitleUpdateSchema = z
  .object({
    regime: vocabulary,
    subtype: vocabulary.nullable(),
    issuedOn: isoDate,
    validUntil: isoDate.nullable(),
    previousTitleId: z.uuid().nullable(),
  })
  .partial()
  .refine((t) => Object.keys(t).length > 0, { message: 'nothing to change' });
export type InOutTitleUpdateInput = z.infer<typeof inoutTitleUpdateSchema>;

/**
 * An absence. With no dates it is the In/Out switch: away from today, until a
 * second touch closes it. With dates it is one written down (setup, a
 * correction): departed and, unless still away, returned.
 */
export const inoutAbsenceCreateSchema = z
  .object({
    departedOn: isoDate.optional(),
    returnedOn: isoDate.nullable().optional(),
    destinationCountry: countryCode.nullable().optional(),
    justificationCode: vocabulary.nullable().optional(),
    note: z.string().max(2000).nullable().optional(),
  })
  .refine((a) => a.departedOn !== undefined || a.returnedOn == null, {
    path: ['returnedOn'],
    message: 'a return needs a departure',
  })
  .refine(
    (a) => a.returnedOn == null || a.departedOn === undefined || a.returnedOn >= a.departedOn,
    {
      path: ['returnedOn'],
      message: 'returnedOn is before departedOn',
    }
  );
export type InOutAbsenceCreateInput = z.infer<typeof inoutAbsenceCreateSchema>;

/** Close (the switch's second touch), correct, name the destination, justify. */
export const inoutAbsenceUpdateSchema = z
  .object({
    departedOn: isoDate,
    /** Null: still away. */
    returnedOn: isoDate.nullable(),
    destinationCountry: countryCode.nullable(),
    justificationCode: vocabulary.nullable(),
    note: z.string().max(2000).nullable(),
  })
  .partial()
  .refine((a) => Object.keys(a).length > 0, { message: 'nothing to change' });
export type InOutAbsenceUpdateInput = z.infer<typeof inoutAbsenceUpdateSchema>;

/**
 * Join a trip to the one it touches (owner, 22 Oct 2026): the days written —
 * a new trip's, or a trip being corrected (`absorbId`, which then goes, its
 * documents moving with it) — and the trip `:id` they share a border day
 * with become one trip; the shared day counts once, as it already did.
 */
export const inoutAbsenceJoinSchema = z
  .strictObject({
    departedOn: isoDate,
    returnedOn: isoDate.nullable(),
    destinationCountry: countryCode.nullable().default(null),
    absorbId: z.uuid().nullable().default(null),
  })
  .refine((a) => a.returnedOn === null || a.returnedOn >= a.departedOn, {
    path: ['returnedOn'],
    message: 'returnedOn is before departedOn',
  });
export type InOutAbsenceJoinInput = z.input<typeof inoutAbsenceJoinSchema>;

/** A file of the person's Vault, as proof or context of an absence (`evidence.items`). */
export const inoutEvidenceAttachSchema = z.object({
  vaultFileId: z.uuid(),
  itemId: vocabulary,
});
export type InOutEvidenceAttachInput = z.infer<typeof inoutEvidenceAttachSchema>;

/** A boarding pass in the person's Vault, to read into the trip it is (6.1). */
export const inoutBoardingPassReadSchema = z.strictObject({ vaultFileId: z.uuid() });
export type InOutBoardingPassReadInput = z.infer<typeof inoutBoardingPassReadSchema>;

const tripShape = z.object({
  departsOn: isoDate,
  returnsOn: isoDate,
  destinationCountry: countryCode.nullable().default(null),
});

/** A trip the person plans (D8) — what lets a notice arrive before they leave. */
export const inoutPlannedTripCreateSchema = tripShape.refine((t) => t.returnsOn >= t.departsOn, {
  path: ['returnsOn'],
  message: 'returnsOn is before departsOn',
});
export type InOutPlannedTripCreateInput = z.infer<typeof inoutPlannedTripCreateSchema>;

/** Move or cancel a planned trip. */
export const inoutPlannedTripUpdateSchema = z
  .object({
    departsOn: isoDate,
    returnsOn: isoDate,
    destinationCountry: countryCode.nullable(),
    state: z.literal('cancelled'),
  })
  .partial()
  .refine((t) => Object.keys(t).length > 0, { message: 'nothing to change' });
export type InOutPlannedTripUpdateInput = z.infer<typeof inoutPlannedTripUpdateSchema>;

/** A trip simulated on the record, never saved. */
export const inoutSimulateSchema = inoutPlannedTripCreateSchema;
export type InOutSimulateInput = InOutPlannedTripCreateInput;

/** The public calculator: the country whose regimes to list. */
export const inoutCalculatorRegimesQuerySchema = z.object({ country: countryCode });
export type InOutCalculatorRegimesQuery = z.infer<typeof inoutCalculatorRegimesQuerySchema>;

/**
 * The public calculator (AC-1): a record as the dashboard's would be — one
 * title, the absences, and optionally the days the person says they were in
 * the host country — evaluated by the same engine, never saved.
 */
export const inoutCalculatorEvaluateSchema = z.object({
  countryCode,
  title: z
    .object({
      regime: vocabulary,
      subtype: vocabulary.nullable().default(null),
      issuedOn: isoDate,
      validUntil: isoDate.nullable(),
    })
    .refine((t) => t.validUntil === null || t.validUntil >= t.issuedOn, {
      path: ['validUntil'],
      message: 'validUntil is before issuedOn',
    }),
  absences: z
    .array(
      z
        .object({
          departedOn: isoDate,
          returnedOn: isoDate.nullable(),
          destinationCountry: countryCode.nullable().default(null),
        })
        .refine((a) => a.returnedOn === null || a.returnedOn >= a.departedOn, {
          path: ['returnedOn'],
          message: 'returnedOn is before departedOn',
        })
    )
    .max(200)
    .default([]),
  /** Periods the person confirms they were in the host country. */
  presentPeriods: z
    .array(
      z
        .object({ periodStart: isoDate, periodEnd: isoDate })
        .refine((p) => p.periodEnd >= p.periodStart, {
          path: ['periodEnd'],
          message: 'periodEnd is before periodStart',
        })
    )
    .max(200)
    .default([]),
  /** The person's calendar day — the client's, as the dashboard reads the person's. */
  today: isoDate,
});
export type InOutCalculatorEvaluateInput = z.infer<typeof inoutCalculatorEvaluateSchema>;

/** The calculator's record and one trip on it, simulated — "what if I travel?". Nothing saved. */
export const inoutCalculatorSimulateSchema = inoutCalculatorEvaluateSchema.extend({
  trip: inoutSimulateSchema,
});
export type InOutCalculatorSimulateInput = z.infer<typeof inoutCalculatorSimulateSchema>;

/**
 * A calculation kept on the public calculator ("Keep my calculation"): the
 * record it described — the day it was read is not part of it. Once there is
 * an account it becomes the person's InOut record, with no setup to retake.
 */
export const inoutKeptCalculationSchema = inoutCalculatorEvaluateSchema.omit({ today: true });
export type InOutKeptCalculationInput = z.infer<typeof inoutKeptCalculationSchema>;

/** The person's notice choices — among the options of the country's rules (checked by the API). */
export const inoutNoticePreferencesSchema = z.object({
  leadTimeDays: z.number().int().positive(),
  confirmationCadence: z.enum(['P15D', 'P30D', 'P1M', 'OFF']),
  pushEnabled: z.boolean(),
  emailEnabled: z.boolean(),
});
export type InOutNoticePreferencesInput = z.infer<typeof inoutNoticePreferencesSchema>;

/** The answer to a presence confirmation. */
export const inoutConfirmationAnswerSchema = z.object({
  answer: z.enum(['IN_HOST', 'WAS_AWAY']),
});
export type InOutConfirmationAnswerInput = z.infer<typeof inoutConfirmationAnswerSchema>;

/** The person approves a purpose of sharing with the agency of their record's country (D2). */
export const inoutConsentGrantSchema = z.object({
  purpose: z.enum(['CONSULTATION', 'REQUISITION']),
});
export type InOutConsentGrantInput = z.infer<typeof inoutConsentGrantSchema>;

/** The longest stretch of the agency's calendar one request reads — a month on screen. */
export const INOUT_AVAILABILITY_MAX_SPAN_DAYS = 31;
const DAY_MS = 86_400_000;

/**
 * The stretch of the agency's calendar to read — instants with their offset
 * (the screen shows the person's own days). The agency's own Bookings rules
 * then narrow it: its minimum lead time and maximum advance.
 */
export const inoutAvailabilityQuerySchema = z
  .object({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
  })
  .refine((q) => Date.parse(q.to) > Date.parse(q.from), {
    path: ['to'],
    message: 'to is not after from',
  })
  .refine(
    (q) => Date.parse(q.to) - Date.parse(q.from) <= INOUT_AVAILABILITY_MAX_SPAN_DAYS * DAY_MS,
    { path: ['to'], message: `the range is longer than ${INOUT_AVAILABILITY_MAX_SPAN_DAYS} days` }
  );
export type InOutAvailabilityQuery = z.infer<typeof inoutAvailabilityQuerySchema>;

/**
 * Buy a consultation at one of the agency's open times — after reading the
 * disclaimer (AC-11): the request carries the person's acceptance.
 */
export const inoutConsultationCheckoutSchema = z.object({
  slotStart: z.iso.datetime({ offset: true }),
  liabilityAccepted: z.literal(true),
});
export type InOutConsultationCheckoutInput = z.infer<typeof inoutConsultationCheckoutSchema>;

/** Move a consultation — or book it again after the agency cancelled — at another open time. */
export const inoutConsultationSlotSchema = z.object({
  slotStart: z.iso.datetime({ offset: true }),
});
export type InOutConsultationSlotInput = z.infer<typeof inoutConsultationSlotSchema>;

/** Buy the requisition — after reading the disclaimer (AC-11). */
export const inoutRequisitionCheckoutSchema = z.object({
  liabilityAccepted: z.literal(true),
});
export type InOutRequisitionCheckoutInput = z.infer<typeof inoutRequisitionCheckoutSchema>;

/** Ask for a refund naming one of the country's reasons (checked by the API). */
export const inoutRefundRequestSchema = z.object({
  reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,38}$/),
});
export type InOutRefundRequestInput = z.infer<typeof inoutRefundRequestSchema>;

// ─── Operations (Tonnie staff, plan §6) ──────────────────────────────────────

const catalogueCode = z.string().regex(/^[A-Z][A-Z0-9_]{1,58}$/);

/** An event an operator registers on a case — catalogue codes only, never free text. */
export const inoutCaseEventSchema = z.object({
  eventCode: catalogueCode,
  occurredAt: z.iso.datetime({ offset: true }),
  noteCode: catalogueCode.nullable(),
});
export type InOutCaseEventInput = z.infer<typeof inoutCaseEventSchema>;

/**
 * The operator's event form: the event, the note (`''` for none) and when it
 * happened on the operator's own clock (`YYYY-MM-DDTHH:mm`, a datetime-local);
 * the screen turns it into the API's instant with its offset.
 */
export const inoutCaseEventFormSchema = z.object({
  eventCode: z.string().min(1, 'business.inoutOperations.event.codeRequired'),
  noteCode: z.string(),
  occurredAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'business.inoutOperations.event.whenRequired'),
});
export type InOutCaseEventFormValues = z.infer<typeof inoutCaseEventFormSchema>;

/** The largest tracking file the operation reads — a month of events is a few kilobytes. */
export const INOUT_TRACKING_FILE_MAX_BYTES = 1_000_000;

/** An agency's tracking file (format v1), as text — the API reads it all again. */
export const inoutTrackingFileSchema = z.object({
  agencyCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,30}$/),
  fileName: z.string().trim().min(1).max(255),
  content: z.string().min(1).max(INOUT_TRACKING_FILE_MAX_BYTES),
});
export type InOutTrackingFileInput = z.infer<typeof inoutTrackingFileSchema>;

/** The month the reconciliation reads, `YYYY-MM`. */
export const inoutReconciliationQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
});
export type InOutReconciliationQuery = z.infer<typeof inoutReconciliationQuerySchema>;

/** The agency whose tracking vocabulary the screen checks a file against. */
export const inoutAgencyQuerySchema = z.object({
  agencyCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,30}$/),
});
export type InOutAgencyQuery = z.infer<typeof inoutAgencyQuerySchema>;

// ─── Forms (journey-web, the phone) ──────────────────────────────────────────
// A form's empty field is `''`; the screen turns it into the API's `null` on
// submit. The order of the dates is checked here, as on the API.

const optionalDate = z.union([isoDate, z.literal('')]);
const optionalCountry = z.union([countryCode, z.literal('')]);

/** An absence written down or corrected — no return date: still away. */
export const inoutAbsenceFormSchema = z
  .object({
    departedOn: isoDate,
    returnedOn: optionalDate,
    destinationCountry: optionalCountry,
  })
  .refine((a) => a.returnedOn === '' || a.returnedOn >= a.departedOn, {
    path: ['returnedOn'],
    message: 'returnedOn is before departedOn',
  });
export type InOutAbsenceFormInput = z.infer<typeof inoutAbsenceFormSchema>;

/** A trip to plan or simulate (D8). */
export const inoutTripFormSchema = z
  .object({
    departsOn: isoDate,
    returnsOn: isoDate,
    destinationCountry: optionalCountry,
  })
  .refine((t) => t.returnsOn >= t.departsOn, {
    path: ['returnsOn'],
    message: 'returnsOn is before departsOn',
  });
export type InOutTripFormInput = z.infer<typeof inoutTripFormSchema>;

/** The title the person holds, as the setup guide and the calculator ask for it. */
export const inoutTitleFormSchema = z
  .object({
    regime: vocabulary,
    subtype: z.union([vocabulary, z.literal('')]),
    issuedOn: isoDate,
    /** Empty for a status with no expiry. */
    validUntil: optionalDate,
  })
  .refine((t) => t.validUntil === '' || t.validUntil >= t.issuedOn, {
    path: ['validUntil'],
    message: 'validUntil is before issuedOn',
  });
export type InOutTitleFormInput = z.infer<typeof inoutTitleFormSchema>;
